import { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { SHUFFLE_INTERVAL_OPTIONS } from '@/lib/api';
import { QueueColumn, LiveIndicator, ShuffleHeroCard, ShuffleUpNextCard, RssInfoModal } from '@/components/producer';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, Select } from '@/components/ui';
import type { QueueItem, GroupedQueueItems } from '@/hooks/useProducerQueue';
import type { PrayerRequest } from '@/types';
import type { ConnectionState } from '@/hooks/useWebSocket';

export type ProducerActiveQueue = 'prayer' | 'praise' | 'combined';

export interface ShuffleLayoutProps {
  prayerQueue: QueueItem[];
  praiseQueue: QueueItem[];
  prayerQueueGrouped: GroupedQueueItems;
  praiseQueueGrouped: GroupedQueueItems;
  isLoading: boolean;
  connectionState: ConnectionState;
  lastPongAt: number | null;
  lastUpdate: Date | null;
  newItemsCount: number;
  clearNewItems: () => void;
  getExportText: (type: 'prayer' | 'praise' | 'all', options?: { groupByCategory: boolean; includeCategoryLabels: boolean }) => string;
  refresh: () => Promise<void>;
  shufflePrayer: boolean;
  shufflePraise: boolean;
  shuffleCombined: boolean;
  shufflePrayerIntervalSeconds: number;
  shufflePraiseIntervalSeconds: number;
  shuffleCombinedIntervalSeconds: number;
  setShuffleState: (update: {
    prayerEnabled?: boolean;
    praiseEnabled?: boolean;
    combinedEnabled?: boolean;
    prayerIntervalSeconds?: number;
    praiseIntervalSeconds?: number;
    combinedIntervalSeconds?: number;
  }) => Promise<void>;
  livePrayerRequestId: string | null;
  livePraiseRequestId: string | null;
  liveCombinedRequestId: string | null;
  liveCombinedQueueType: 'prayer' | 'praise' | null;
  shufflePrayerNextRequestId: string | null;
  shufflePraiseNextRequestId: string | null;
  shufflePrayerNextIds: string[];
  shufflePraiseNextIds: string[];
  shuffleCombinedNextIds: Array<{ id: string; queueType: 'prayer' | 'praise' | string }>;
  liveIsManualOverridePrayer: boolean;
  liveIsManualOverridePraise: boolean;
  setLivePrayerRequestId: (id: string | null) => void;
  setLivePraiseRequestId: (id: string | null) => void;
  setLiveCombinedRequestId: (id: string | null) => void;
  markLiveAsManualOverridePrayer: () => void;
  markLiveAsManualOverridePraise: () => void;
  editingBy: Record<string, { userId: string; userName: string }>;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
  copySync: (text: string) => boolean;
  onRemove: (prayerId: string) => Promise<void>;
  onClearPrayerQueue: () => Promise<void>;
  onClearPraiseQueue: () => Promise<void>;
  onDisplayLive: (prayerRequestId: string) => void;
  onDisplayLivePraise: (prayerRequestId: string) => void;
  onEdit: (prayer: PrayerRequest) => void;
  currentUserId: string | null;
  /** Controlled: which queue is active */
  activeQueue: ProducerActiveQueue;
  onActiveQueueChange: (queue: ProducerActiveQueue) => void;
}

function typeLabelFor(queueType: 'prayer' | 'praise' | string): string {
  return queueType === 'praise' ? 'Answered Prayer' : 'Prayer Request';
}

export function ShuffleLayout({
  prayerQueue,
  praiseQueue,
  prayerQueueGrouped,
  praiseQueueGrouped,
  isLoading,
  connectionState,
  lastPongAt,
  lastUpdate,
  newItemsCount,
  clearNewItems,
  getExportText,
  refresh: _refresh,
  shufflePrayer,
  shufflePraise,
  shuffleCombined,
  shufflePrayerIntervalSeconds,
  shufflePraiseIntervalSeconds,
  shuffleCombinedIntervalSeconds,
  setShuffleState,
  livePrayerRequestId,
  livePraiseRequestId,
  liveCombinedRequestId,
  liveCombinedQueueType,
  shufflePrayerNextRequestId,
  shufflePraiseNextRequestId,
  shufflePrayerNextIds,
  shufflePraiseNextIds,
  shuffleCombinedNextIds,
  liveIsManualOverridePrayer,
  liveIsManualOverridePraise,
  setLivePrayerRequestId: _setLivePrayerRequestId,
  setLivePraiseRequestId: _setLivePraiseRequestId,
  setLiveCombinedRequestId: _setLiveCombinedRequestId,
  markLiveAsManualOverridePrayer,
  markLiveAsManualOverridePraise,
  editingBy,
  addToast,
  copySync,
  onRemove,
  onClearPrayerQueue,
  onClearPraiseQueue,
  onDisplayLive,
  onDisplayLivePraise,
  onEdit,
  currentUserId,
  activeQueue,
  onActiveQueueChange,
}: ShuffleLayoutProps) {
  const [isRssInfoOpen, setIsRssInfoOpen] = useState(false);
  const isCombined = activeQueue === 'combined';

  const items = activeQueue === 'prayer' ? prayerQueue : activeQueue === 'praise' ? praiseQueue : [];
  const groupedItems = activeQueue === 'prayer' ? prayerQueueGrouped : activeQueue === 'praise' ? praiseQueueGrouped : {};
  const liveId = isCombined
    ? liveCombinedRequestId
    : activeQueue === 'prayer'
      ? livePrayerRequestId
      : livePraiseRequestId;
  const nextIds = isCombined
    ? shuffleCombinedNextIds.map((row) => row.id)
    : activeQueue === 'prayer'
      ? shufflePrayerNextIds
      : shufflePraiseNextIds;
  const shuffleEnabled = isCombined
    ? shuffleCombined
    : activeQueue === 'prayer'
      ? shufflePrayer
      : shufflePraise;
  const shuffleIntervalSeconds = isCombined
    ? shuffleCombinedIntervalSeconds
    : activeQueue === 'prayer'
      ? shufflePrayerIntervalSeconds
      : shufflePraiseIntervalSeconds;

  const allItems = useMemo(
    () => [...prayerQueue, ...praiseQueue],
    [prayerQueue, praiseQueue]
  );

  const liveItem = useMemo(() => {
    if (!liveId) return null;
    if (isCombined) {
      return allItems.find((i) => i.prayerRequestId === liveId) ?? null;
    }
    return items.find((i) => i.prayerRequestId === liveId) ?? null;
  }, [liveId, isCombined, allItems, items]);

  const liveTypeLabel = isCombined
    ? typeLabelFor(liveCombinedQueueType ?? liveItem?.queueType ?? 'prayer')
    : undefined;

  /** Drop current live and consecutive duplicates so Live / Up next never mirror the same id from a stale payload. */
  const normalizedNextIds = useMemo(() => {
    const liveStr = liveId != null ? String(liveId) : null;
    const seen = new Set<string>();
    const out: string[] = [];
    for (const raw of nextIds) {
      const id = String(raw);
      if (liveStr !== null && id === liveStr) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      if (out.length >= 2) break;
    }
    return out;
  }, [nextIds, liveId]);

  const upNextItems = useMemo(() => {
    const source = isCombined ? allItems : items;
    return normalizedNextIds
      .map((id) => source.find((i) => String(i.prayerRequestId) === id))
      .filter((i): i is QueueItem => i != null);
  }, [normalizedNextIds, isCombined, allItems, items]);

  const upNextTypeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of shuffleCombinedNextIds) {
      map.set(String(row.id), row.queueType);
    }
    return map;
  }, [shuffleCombinedNextIds]);

  const title = activeQueue === 'prayer' ? 'Prayer Requests' : activeQueue === 'praise' ? 'Answered Prayers' : 'Combined';
  const shuffleItemCount = isCombined ? prayerQueue.length + praiseQueue.length : items.length;

  const handleDisplayForItem = (item: QueueItem) => {
    if (item.queueType === 'praise') {
      onDisplayLivePraise(item.prayerRequestId);
      markLiveAsManualOverridePraise();
    } else {
      onDisplayLive(item.prayerRequestId);
      markLiveAsManualOverridePrayer();
    }
  };

  const toggleButtons: { key: ProducerActiveQueue; label: string }[] = [
    { key: 'prayer', label: 'Prayer Requests' },
    { key: 'praise', label: 'Answered Prayers' },
    { key: 'combined', label: 'Combined' },
  ];

  return (
    <>
      <PageHeader
        title="Live Queue"
        description={
          isCombined
            ? 'Mixed prayer and answered-prayer feed with side-by-side source queues'
            : 'One queue at a time with live and up-next preview'
        }
        actions={
          <>
            <LiveIndicator
              connectionState={connectionState}
              lastPongAt={lastPongAt}
              lastUpdate={lastUpdate}
              newItemsCount={newItemsCount}
              onClearNewItems={clearNewItems}
            />
            <div className="flex rounded-lg border border-slate-200 dark:border-slate-600 overflow-hidden">
              {toggleButtons.map((btn) => (
                <button
                  key={btn.key}
                  type="button"
                  onClick={() => onActiveQueueChange(btn.key)}
                  className={cn(
                    'px-3 py-1.5 text-sm font-medium',
                    activeQueue === btn.key
                      ? 'bg-primary-600 text-white dark:bg-primary-500 dark:text-slate-900'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                  )}
                >
                  {btn.label}
                </button>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setIsRssInfoOpen(true)}
            >
              RSS Info
            </Button>
          </>
        }
      />

      <RssInfoModal isOpen={isRssInfoOpen} onClose={() => setIsRssInfoOpen(false)} />

      <div className="space-y-6">
        <section>
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
              Live now
            </h3>
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={shuffleEnabled}
                onClick={() =>
                  setShuffleState(
                    isCombined
                      ? { combinedEnabled: !shuffleCombined }
                      : activeQueue === 'prayer'
                        ? { prayerEnabled: !shufflePrayer }
                        : { praiseEnabled: !shufflePraise }
                  )
                }
                disabled={shuffleItemCount === 0}
                className={cn(
                  'inline-flex items-center gap-2 text-sm',
                  shuffleItemCount === 0
                    ? 'text-slate-400 dark:text-slate-600 cursor-not-allowed'
                    : 'text-slate-700 dark:text-slate-200'
                )}
                title={shuffleEnabled ? 'Turn shuffle off' : 'Turn shuffle on'}
              >
                <span>Shuffle</span>
                <span
                  className={cn(
                    'relative inline-flex h-5 w-9 items-center rounded-full border transition-colors',
                    shuffleEnabled
                      ? 'border-emerald-500 bg-emerald-500'
                      : 'border-slate-300 bg-slate-200 dark:border-slate-600 dark:bg-slate-700'
                  )}
                >
                  <span
                    className={cn(
                      'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                      shuffleEnabled ? 'translate-x-4' : 'translate-x-0.5'
                    )}
                  />
                </span>
              </button>
              <div className="min-w-[5rem]">
                <Select
                  label=""
                  options={SHUFFLE_INTERVAL_OPTIONS.map(seconds => ({
                    value: String(seconds),
                    label: seconds >= 60 ? `${seconds / 60}m` : `${seconds}s`,
                  }))}
                  value={String(shuffleIntervalSeconds)}
                  onChange={e =>
                    setShuffleState(
                      isCombined
                        ? { combinedIntervalSeconds: Number(e.target.value) }
                        : activeQueue === 'prayer'
                          ? { prayerIntervalSeconds: Number(e.target.value) }
                          : { praiseIntervalSeconds: Number(e.target.value) }
                    )
                  }
                  className="py-1.5 text-sm min-h-0 h-[36px]"
                  aria-label="Shuffle interval"
                  disabled={shuffleItemCount === 0}
                />
              </div>
            </div>
          </div>
          <ShuffleHeroCard
            item={liveItem}
            queueType={
              isCombined
                ? (liveCombinedQueueType ?? liveItem?.queueType ?? 'prayer')
                : activeQueue
            }
            typeLabel={liveTypeLabel}
            onDisplayAgain={
              liveItem
                ? () => handleDisplayForItem(liveItem)
                : undefined
            }
            onEdit={onEdit}
            onRemove={onRemove}
          />
        </section>

        {shuffleEnabled && (
          <section>
            <div className="mb-3 flex items-center gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                Up next
              </h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {upNextItems.length}/2 queued
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[0, 1].map((idx) => {
                const upItem = upNextItems[idx] ?? null;
                const queueType =
                  upItem?.queueType ??
                  (isCombined
                    ? (upNextTypeById.get(normalizedNextIds[idx] ?? '') as 'prayer' | 'praise' | undefined) ??
                      'prayer'
                    : activeQueue);
                return (
                  <ShuffleUpNextCard
                    key={`upnext-${idx}`}
                    item={upItem}
                    queueType={queueType === 'praise' ? 'praise' : 'prayer'}
                    typeLabel={isCombined ? typeLabelFor(queueType) : undefined}
                    position={idx + 1}
                    onDisplay={(id) => {
                      const found =
                        upItem ??
                        allItems.find((i) => i.prayerRequestId === id) ??
                        null;
                      if (found) {
                        handleDisplayForItem(found);
                      } else if (isCombined ? queueType === 'praise' : activeQueue === 'praise') {
                        onDisplayLivePraise(id);
                        markLiveAsManualOverridePraise();
                      } else {
                        onDisplayLive(id);
                        markLiveAsManualOverridePrayer();
                      }
                    }}
                    onEdit={onEdit}
                    onRemove={onRemove}
                  />
                );
              })}
            </div>
          </section>
        )}

        {isCombined ? (
          <section>
            <div className="mb-3 flex items-center gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                Source queues
              </h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {prayerQueue.length + praiseQueue.length} total cards
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <QueueColumn
                title="Prayer Requests"
                queueType="prayer"
                items={prayerQueue}
                groupedItems={prayerQueueGrouped}
                isLoading={isLoading}
                onRemove={onRemove}
                groupByCategory={true}
                defaultCollapsedToAll={true}
                livePrayerRequestId={livePrayerRequestId}
                shufflePrayerNextRequestId={shufflePrayerNextRequestId}
                showNextInShuffle={liveIsManualOverridePrayer}
                onDisplayLive={onDisplayLive}
                onCopyQueue={() => {
                  const text = getExportText('prayer', { groupByCategory: true, includeCategoryLabels: true });
                  const ok = copySync(text);
                  addToast(ok ? 'Copied to clipboard' : 'Failed to copy', ok ? 'success' : 'error');
                }}
                onClearAll={onClearPrayerQueue}
                onEdit={onEdit}
                editingBy={editingBy}
                currentUserId={currentUserId}
              />
              <QueueColumn
                title="Answered Prayers"
                queueType="praise"
                items={praiseQueue}
                groupedItems={praiseQueueGrouped}
                isLoading={isLoading}
                onRemove={onRemove}
                groupByCategory={true}
                defaultCollapsedToAll={true}
                livePraiseRequestId={livePraiseRequestId}
                shufflePraiseNextRequestId={shufflePraiseNextRequestId}
                showNextInShuffle={liveIsManualOverridePraise}
                onDisplayLivePraise={onDisplayLivePraise}
                onCopyQueue={() => {
                  const text = getExportText('praise', { groupByCategory: true, includeCategoryLabels: true });
                  const ok = copySync(text);
                  addToast(ok ? 'Copied to clipboard' : 'Failed to copy', ok ? 'success' : 'error');
                }}
                onClearAll={onClearPraiseQueue}
                onEdit={onEdit}
                editingBy={editingBy}
                currentUserId={currentUserId}
              />
            </div>
          </section>
        ) : (
          <section>
            <div className="mb-3 flex items-center gap-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                Remaining
              </h3>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                {items.length} total cards
              </span>
            </div>
            <QueueColumn
              title={title}
              queueType={activeQueue}
              items={items}
              groupedItems={groupedItems}
              isLoading={isLoading}
              onRemove={onRemove}
              groupByCategory={true}
              defaultCollapsedToAll={true}
              livePrayerRequestId={activeQueue === 'prayer' ? livePrayerRequestId : undefined}
              livePraiseRequestId={activeQueue === 'praise' ? livePraiseRequestId : undefined}
              shufflePrayerNextRequestId={activeQueue === 'prayer' ? shufflePrayerNextRequestId : undefined}
              shufflePraiseNextRequestId={activeQueue === 'praise' ? shufflePraiseNextRequestId : undefined}
              showNextInShuffle={activeQueue === 'prayer' ? liveIsManualOverridePrayer : liveIsManualOverridePraise}
              onDisplayLive={activeQueue === 'prayer' ? onDisplayLive : undefined}
              onDisplayLivePraise={activeQueue === 'praise' ? onDisplayLivePraise : undefined}
              onCopyQueue={() => {
                const text = getExportText(activeQueue, { groupByCategory: true, includeCategoryLabels: true });
                const ok = copySync(text);
                addToast(ok ? 'Copied to clipboard' : 'Failed to copy', ok ? 'success' : 'error');
              }}
              onClearAll={activeQueue === 'prayer' ? onClearPrayerQueue : onClearPraiseQueue}
              onEdit={onEdit}
              editingBy={editingBy}
              currentUserId={currentUserId}
            />
          </section>
        )}
      </div>
    </>
  );
}
